import React, { useEffect, useRef, useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGithub } from '@fortawesome/free-brands-svg-icons';
import {
    faFileArrowUp,
    faFileWord,
    faDownload,
    faCopy,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import useDocumentTitle from '../hooks/useDocumentTitle';
import docxToMarkdown from '../utils/docxToMarkdown';
import styles from './css/DocX2MD.module.css';

function DocX2MD() {
    useDocumentTitle('DocX2MD — Word to Markdown');

    // Arriving from the Projects card mid-scroll; start at the top.
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const inputRef = useRef(null);
    const [dragActive, setDragActive] = useState(false);
    const [converting, setConverting] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState(null); // { fileName, markdown, warnings }
    const [view, setView] = useState('preview');
    const [copied, setCopied] = useState(false);

    const convertFile = async (file) => {
        if (!file) return;
        if (!file.name.toLowerCase().endsWith('.docx')) {
            setError('Only .docx files are supported.');
            return;
        }
        setError('');
        setConverting(true);
        try {
            const buffer = await file.arrayBuffer();
            const { markdown, warnings } = await docxToMarkdown(buffer);
            setResult({
                fileName: file.name.replace(/\.docx$/i, ''),
                markdown,
                warnings,
            });
            setView('preview');
        } catch (e) {
            setResult(null);
            setError('Conversion failed — the file may be corrupt or not a valid Word document.');
        } finally {
            setConverting(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        convertFile(e.dataTransfer.files?.[0]);
    };

    const downloadMarkdown = () => {
        const blob = new Blob([result.markdown], { type: 'text/markdown;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${result.fileName}.md`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyMarkdown = async () => {
        try {
            await navigator.clipboard.writeText(result.markdown);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* clipboard unavailable; ignore */
        }
    };

    const previewHtml = result
        ? DOMPurify.sanitize(marked.parse(result.markdown, { gfm: true }))
        : '';

    return (
        <div className={styles.pageContainer}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>DocX2MD</h2>
                <div className={styles.sectionDivider} />
            </div>
            <p className={styles.lede}>
                Convert a Word document to clean, wiki-ready Markdown — headings,
                lists, tables, images, links, and code formatting preserved.
                Everything runs in your browser; the file never leaves your
                computer.{' '}
                <a
                    href="https://github.com/Tishinator/DocX2MD"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <FontAwesomeIcon icon={faGithub} /> View source
                </a>
            </p>

            <div
                className={`${styles.dropzone} ${dragActive ? styles.dropzoneActive : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && inputRef.current?.click()}
            >
                <FontAwesomeIcon icon={faFileArrowUp} className={styles.dropzoneIcon} />
                <p className={styles.dropzoneText}>
                    {converting ? 'Converting…' : 'Drop a .docx file here, or click to browse'}
                </p>
                <p className={styles.dropzoneHint}>
                    Images are embedded into the Markdown as base64 data
                </p>
                <input
                    ref={inputRef}
                    type="file"
                    accept=".docx"
                    className={styles.hiddenInput}
                    onChange={(e) => { convertFile(e.target.files?.[0]); e.target.value = ''; }}
                />
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            {result && (
                <div className={styles.resultPanel}>
                    <div className={styles.toolbar}>
                        <span className={styles.fileName}>
                            <FontAwesomeIcon icon={faFileWord} />
                            {result.fileName}.md
                        </span>
                        <div className={styles.toolbarActions}>
                            <button
                                className={`${styles.tabButton} ${view === 'preview' ? styles.tabButtonActive : ''}`}
                                onClick={() => setView('preview')}
                            >
                                Preview
                            </button>
                            <button
                                className={`${styles.tabButton} ${view === 'raw' ? styles.tabButtonActive : ''}`}
                                onClick={() => setView('raw')}
                            >
                                Markdown
                            </button>
                            <button className={styles.actionButton} onClick={copyMarkdown}>
                                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                                {copied ? 'Copied' : 'Copy'}
                            </button>
                            <button
                                className={`${styles.actionButton} ${styles.primaryButton}`}
                                onClick={downloadMarkdown}
                            >
                                <FontAwesomeIcon icon={faDownload} />
                                Download .md
                            </button>
                        </div>
                    </div>

                    <div className={styles.viewBody}>
                        {view === 'preview' ? (
                            <div
                                className={styles.markdownBody}
                                dangerouslySetInnerHTML={{ __html: previewHtml }}
                            />
                        ) : (
                            <pre className={styles.rawView}>{result.markdown}</pre>
                        )}
                    </div>

                    {result.warnings.length > 0 && (
                        <ul className={styles.warningsBox}>
                            {result.warnings.map((w, i) => (
                                <li key={i}>⚠ {w}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}

export default DocX2MD;
